import { type Dayjs } from 'dayjs';
import Decimal from 'decimal.js';
import {
  fetchEvp,
  fetchBusinessunits,
  fetchCommodities,
  fetchCountries,
  fetchGridLocations,
  fetchTechSources,
  fetchTSproductOptions,
  fetchTypeOfSupplyByBusiness,
  fetchCountryAssetOfSupplyByType,
} from '../../../api/services';
import {
  FETCH_ERROR,
  GAS_FIRED_GENERATION,
  GRID,
  KBBL,
  MMBTU,
  MWH,
  NATURAL_GAS,
  PIPELINE_GAS,
  PJ,
  POWER,
  RNG,
  AMERICAS,
  EUROPE,
  ASIA,
  AUSTRALIA,
  GLOBAL,
  AFRICA,
  DR_POWER_OTHERS,
  TBTU,
  TYPE_OF_SUPPLY_COUNTRY_MAPPING,
  allowedCommoditiesForSEandDRevp,
  SLMT_COMMODITY_OPTIONS,
  techSourcePermitted,
  allowedCommoditesForSlmtRevp,
} from '../../../api/constants';
import {
  convertInputToCommaSeparatedWithTwoDecimal,
  regexForOneToHundredUptoTwoDecimals,
} from '../../../utils';
import filterOptions from '../../../constants/filterOptions';
import {
  type ICalculatorFormState,
  TChangeEvent,
  Options,
  IDispatch,
  Action,
  EventValue,
  ArrayDispatch,
  BooleanDispatch,
  StringDispatch,
  ICountry,
  IGridLocation,
  IManualFormData,
  ITechSrcApiResponseObj,
  eventType,
  IOptions,
} from '../../../types/types';
import { four, hundred, three } from '../../../constants';
import { notify } from '../../Toast/notify';
import { capitalizeFirstLetter } from '../../AllDeals/DealDetails/DealDetailFunctions';

const possibleCountryOptions = filterOptions.possibleCountryOptions;

// Helper function to normalize EVP name for comparison
const normalizeEvpName = (evpName: string): string => {
  return evpName.toLowerCase().replace(/[^a-z0-9]/g, '');
};

// Helper function to reset all dependent fields on EVP change
const resetDependentFields = (
  dispatch: IDispatch,
  myDispatch: IDispatch,
  isTSProducts: boolean,
  setCountry: StringDispatch,
  setGridLocationOptions: ArrayDispatch,
) => {
  setCountry('');
  setGridLocationOptions([]);

  dispatch({ type: 'setBusiness', payload: isTSProducts ? [] : '' });
  dispatch({ type: 'setBusinessId', payload: 0 });
  dispatch({ type: 'setTypeOfSupply', payload: '' });
  dispatch({ type: 'setCommodity', payload: isTSProducts ? [] : '' });
  dispatch({ type: 'setCommodityId', payload: 0 });
  dispatch({ type: 'setRegion', payload: '' });
  dispatch({ type: 'setRegionOptions', payload: [] });
  dispatch({ type: 'setCountry', payload: '' });
  dispatch({ type: 'setCountryId', payload: 0 });
  dispatch({ type: 'setCountryOptions', payload: [] });
  dispatch({ type: 'setGridLocation', payload: '' });
  dispatch({ type: 'setGridLocationId', payload: 0 });
  dispatch({ type: 'setAssetType', payload: '' });
  dispatch({ type: 'setFuelType', payload: '' });
  dispatch({ type: 'setOperatingModel', payload: '' });
  dispatch({ type: 'setEfficiency', payload: '' });
  dispatch({ type: 'setEquityShare', payload: '' });

  myDispatch({ type: 'setTechSource', payload: [] });
  myDispatch({ type: 'setShowGasFields', payload: false });
  myDispatch({ type: 'setDisableGrid', payload: true });
  myDispatch({ type: 'setEnableTechSourceField', payload: false });
  myDispatch({ type: 'setIsTypeOfSupplyValid', payload: true });
  myDispatch({ type: 'setisRegionValid', payload: true });
  myDispatch({ type: 'setisCountryValid', payload: true });
  myDispatch({ type: 'setisCommodityValid', payload: true });
  myDispatch({ type: 'setisBusinessValid', payload: true });
};

// Helper function to fetch commodity options based on EVP
const fetchCommodityOptions = async (normalizedEvp: string): Promise<IOptions[]> => {
  let commodityOrProductOptions: IOptions[] = [];

  try {
    if (normalizedEvp === 'tsproducts') {
      const response = await fetchTSproductOptions();
      commodityOrProductOptions = response.data.map(
        (product: { PRODUCT_NAME: string; PRODUCT_ID: number }) => ({
          value: product.PRODUCT_ID,
          label: product.PRODUCT_NAME,
        }),
      );
    } else {
      const response = await fetchCommodities();
      for (const option of response.data) {
        const commodityName = option.TRADE_COMMODITY_NAME;
        const commodityInLowerCase = commodityName.replace(' ', '').toLowerCase();
        const isValidCommodity = (normalizedEvp === 'slmt') ? allowedCommoditesForSlmtRevp.includes(commodityInLowerCase) : allowedCommoditiesForSEandDRevp.includes(commodityInLowerCase);
        const { TRADE_COMMODITY_ID, TRADE_COMMODITY_NAME } = option;
        if (isValidCommodity) {
          commodityOrProductOptions.push({
            value: TRADE_COMMODITY_ID,
            label: TRADE_COMMODITY_NAME,
          });
        }
      }
    }
  } catch {
    notify('error', FETCH_ERROR);
  }

  return commodityOrProductOptions;
};

export const onGrossMarginChange = (
  e: TChangeEvent,
  dispatch: IDispatch,
  myDispatch: IDispatch,
) => {
  // set validation to false if value changes
  dispatch({
    type: 'setIsFormValidated',
    payload: false,
  });
  const grossMarginValInput = e.target.value;
  const valueToShowInUI = convertInputToCommaSeparatedWithTwoDecimal(grossMarginValInput);
  myDispatch({
    type: 'setGrossMarginVal',
    payload: valueToShowInUI,
  });
  dispatch({
    type: 'setGrossMargin',
    payload: new Decimal(valueToShowInUI.replaceAll(',', '')),
  });
  myDispatch({
    type: 'setisGrossMarginValid',
    payload: true,
  });
};

export const onOpportunityChange = (
  e: TChangeEvent,
  dispatch: IDispatch,
  myDispatch: IDispatch,
) => {
  // set validation to false if value changes
  dispatch({
    type: 'setIsFormValidated',
    payload: false,
  });
  const value = e.target.value;
  if (value) {
    myDispatch({
      type: 'setisOpportunityValid',
      payload: true,
    });
    dispatch({
      type: 'setOpportunity',
      payload: value,
    });
  } else {
    myDispatch({
      type: 'setisOpportunityValid',
      payload: false,
    });
  }
};

export const onEquityPercentageChange = (
  e: TChangeEvent,
  dispatch: IDispatch,
  myDispatch: IDispatch,
) => {
  // invalidate the form if value is changed from one option to another,
  // this will lead to hide the step two table
  dispatch({
    type: 'setIsFormValidated',
    payload: false,
  });

  const inputValue = e.target.value;
  if (regexForOneToHundredUptoTwoDecimals.test(inputValue)) {
    myDispatch({
      type: 'setEquityShareVal',
      payload: inputValue,
    });
    myDispatch({
      type: 'setIsEquityPercentageValid',
      payload: true,
    });
    dispatch({
      type: 'setEquityShare',
      payload: Number(inputValue) / hundred,
    });
    return;
  }
  if (inputValue === '0' || inputValue === '') {
    myDispatch({
      type: 'setEquityShareVal',
      payload: '',
    });
    dispatch({
      type: 'setEquityShare',
      payload: '',
    });
  }
};

export const onEfficiencyPercentageChange = (
  e: TChangeEvent,
  dispatch: IDispatch,
  myDispatch: IDispatch,
) => {
  // invalidate the form if value is changed from one option to another,
  // this will lead to hide the step two table
  dispatch({
    type: 'setIsFormValidated',
    payload: false,
  });

  const inputValue = e.target.value;
  if (regexForOneToHundredUptoTwoDecimals.test(inputValue)) {
    myDispatch({
      type: 'setEfficiencyVal',
      payload: inputValue,
    });
    myDispatch({
      type: 'setIsEfficiencyValid',
      payload: true,
    });

    dispatch({
      type: 'setEfficiency',
      payload: Number(inputValue) / hundred,
    });
    return;
  }
  if (inputValue === '0' || inputValue === '') {
    myDispatch({
      type: 'setEfficiencyVal',
      payload: '',
    });
    dispatch({
      type: 'setEfficiency',
      payload: '',
    });
  }
};

export const handleAssetTypeChange = (e: eventType, dispatch: IDispatch, myDispatch: IDispatch) => {
  // invalidate the form if value is changed from one option to another, this will lead to hide the step two table
  dispatch({ type: 'setIsFormValidated', payload: false });
  // update the selected asset type
  dispatch({ type: 'setAssetType', payload: e as string });
  // update the asset type is valid
  myDispatch({ type: 'setIsAssetTypeValid', payload: true });
};

export const handleFuelTypeChange = (
  e: eventType,
  dispatch: IDispatch,
  myDispatch: IDispatch,
  state: ICalculatorFormState,
  myState: IManualFormData,
) => {
  // invalidate the form if value is changed from one option to another, this will lead to hide the step two table
  dispatch({
    type: 'setIsFormValidated',
    payload: false,
  });

  // set technology sources for saving kpi data
  const selectedTechSources = [...state.selectedTechnologySources];
  const updatedSelectedTechSources = selectedTechSources.map((item) => {
    if ([GAS_FIRED_GENERATION, 'LNG', NATURAL_GAS].includes(item.value)) {
      // Find the item in resp matching to LNG and get its id
      const fuelTypeTech = myState.techSourceApiResponse.find(
        (x: { TECHNOLOGY_SOURCE_NAME: string }) => x.TECHNOLOGY_SOURCE_NAME === e,
      );
      if (fuelTypeTech) {
        // Return a new object with the updated value and the matching id
        return {
          value: e,
          id: fuelTypeTech.TECHNOLOGY_SOURCE_ID,
        };
      }
    }
    // Return the original object for all other cases
    return item;
  });
  dispatch({
    type: 'setSelectedTechnologySources',
    payload: updatedSelectedTechSources as { value: string; id: number }[],
  });

  dispatch({
    type: 'setFuelType',
    payload: e as string,
  });
  myDispatch({
    type: 'setIsFuelTypeValid',
    payload: true,
  });
};

export const handleOperatingModelChange = (
  _e: eventType,
  options: Options,
  dispatch: IDispatch,
  myDispatch: IDispatch,
) => {
  // invalidate the form if value is changed from one option to another, this will lead to hide the step two table
  dispatch({ type: 'setIsFormValidated', payload: false });
  myDispatch({ type: 'setIsOperatingModelValid', payload: true });
  dispatch({ type: 'setOperatingModel', payload: Number(options.key) });

  if (Number(options.key) === three) {
    // for Operating Model as Toll PPA, equity share is default to 0
    myDispatch({ type: 'setEquityShareVal', payload: '0' });
    dispatch({ type: 'setEquityShare', payload: 0 / hundred });
    myDispatch({ type: 'setShowEquityShare', payload: false });
    myDispatch({ type: 'setIsEquityPercentageValid', payload: true });
    return;
  }

  if (Number(options.key) === four) {
    // for Operating Model as Lease, equity share is default to 100%
    myDispatch({ type: 'setEquityShareVal', payload: '100' });
    dispatch({ type: 'setEquityShare', payload: 1 });
    myDispatch({ type: 'setShowEquityShare', payload: false });
    myDispatch({ type: 'setIsEquityPercentageValid', payload: true });
    return;
  }
  // for Operating Model SOV/NOV, set equity share to empty and enable the field.
  myDispatch({ type: 'setEquityShareVal', payload: '' });
  dispatch({ type: 'setEquityShare', payload: '' });
  myDispatch({ type: 'setShowEquityShare', payload: true });
};
export const handleStartDateChange = (
  e: TChangeEvent | EventValue<Dayjs> | { $d: Date },
  dispatch: IDispatch,
  myDispatch: IDispatch,
) => {
  // invalidate the form if value is changed from one option to another, this will lead to hide the step two table
  dispatch({
    type: 'setIsFormValidated',
    payload: false,
  });
  // reset the end date whenever there is a start date change
  myDispatch({
    type: 'setEndDate',
    payload: '',
  });
  if (e && '$d' in e && e.$d !== undefined) {
    myDispatch({
      type: 'setStartDate',
      payload: e.$d.toString(),
    });
    dispatch({
      type: 'setStartYear',
      payload: e.$d.getFullYear(),
    });
    dispatch({
      type: 'setStartDate',
      payload: e.$d.toString(),
    });
    myDispatch({
      type: 'setisStartDateValid',
      payload: true,
    });
  } else {
    myDispatch({
      type: 'setisStartDateValid',
      payload: false,
    });
  }
};

export const handleEndDateChange = (
  e: TChangeEvent | EventValue<Dayjs> | { $d: Date },
  dispatch: IDispatch,
  myDispatch: IDispatch,
) => {
  // invalidate the form if value is changed from one option to another, this will lead to hide the step two table
  dispatch({
    type: 'setIsFormValidated',
    payload: false,
  });
  if (e && '$d' in e && e.$d !== undefined) {
    myDispatch({
      type: 'setEndDate',
      payload: e.$d.toString(),
    });
    dispatch({
      type: 'setEndYear',
      payload: e.$d.getFullYear(),
    });
    dispatch({
      type: 'setEndDate',
      payload: e.$d.toString(),
    });
    myDispatch({
      type: 'setisEndDateValid',
      payload: true,
    });
  } else {
    myDispatch({
      type: 'setisEndDateValid',
      payload: false,
    });
  }
};

export const handleEvpChange = async (
  _e: eventType,
  data: { option: Options; myState: IManualFormData },
  dispatch: IDispatch,
  myDispatch: IDispatch,
  func: {
    setGridLocationOptions: ArrayDispatch;
    setCountry: StringDispatch;
  },
) => {
  const { option, myState } = data;
  const { setGridLocationOptions, setCountry } = func;

  // invalidate the form if value is changed from one option to another
  dispatch({
    type: 'setIsFormValidated',
    payload: false,
  });

  const evpValue = option.value as string;
  const normalizedEvp = normalizeEvpName(evpValue);

  // Find the selected EVP from evpOptions to get the key (EVP_ID)
  const selectedEvpOption = myState.evpOptions.find(
    (opt) => opt.value === evpValue || opt.label === evpValue,
  );
  const evpId =
    selectedEvpOption && selectedEvpOption.key ? Number(selectedEvpOption.key) : undefined;

  dispatch({ type: 'setEvp', payload: evpValue });
  if (evpId) {
    dispatch({ type: 'setEvpId', payload: evpId });
  }

  if (evpValue !== '' && evpValue !== null) {
    myDispatch({
      type: 'setisEvpValid',
      payload: true,
    });
  } else {
    myDispatch({
      type: 'setisEvpValid',
      payload: false,
    });
  }

  // Reset all dependent fields
  const isTSProducts = normalizedEvp === 'tsproducts';
  resetDependentFields(dispatch, myDispatch, isTSProducts, setCountry, setGridLocationOptions);

  // Show loading immediately when EVP changes
  myDispatch({
    type: 'setIsLoading',
    payload: true,
  });

  // Set commodity or product options
  const commodityOrProductOptions = await fetchCommodityOptions(normalizedEvp);
  myDispatch({ type: 'setCommodityOptions', payload: commodityOrProductOptions });
  // Fetch business units from API
  try {
    const businessunits = await fetchBusinessunits(evpId);
    const businessunitsOptn = [];

    for (const option of businessunits.data) {
      businessunitsOptn.push({
        value: option.BUSINESS_ID,
        label: option.BUSINESS_NAME,
      });
    }

    myDispatch({ type: 'setBusinessunitsOptns', payload: businessunitsOptn });
  } catch {
    notify('error', FETCH_ERROR);
  } finally {
    myDispatch({
      type: 'setIsLoading',
      payload: false,
    });
  }
};


export const handleBusinessChange = async (
  _e: eventType,
  data: { myState: IManualFormData; option: Options; state: ICalculatorFormState; country: string },
  dispatch: IDispatch,
  myDispatch: IDispatch,
  func: {
    setGridLocationOptions: ArrayDispatch;
    setIsFetchingCountry: BooleanDispatch;
    setCountry: StringDispatch;
    setIsFetchingGridLocation: BooleanDispatch;
  },
) => {
  const { myState, option, state, country } = data;
  const { setGridLocationOptions, setIsFetchingCountry, setCountry, setIsFetchingGridLocation } = func;
  let regions: string[] = [];
  dispatch({ type: 'setIsFormValidated', payload: false });
  const normalizedEvp = normalizeEvpName(state.evp);
  const isMultiSelect = normalizedEvp === 'tsproducts';
  const buz = isMultiSelect ? (_e as string[]) : (option.value as string);
  const businessLabels = Array.isArray(buz) ? buz : [buz];
  const businessIds = businessLabels
    .map((label) => myState.businessunitsOptns.find((opt) => String(opt.label) === String(label))?.value)
    .filter((value): value is string | number => value !== undefined && value !== null)
    .map((value) => Number(value))
    .filter((value) => !Number.isNaN(value));
  const businessForRegion = isMultiSelect ? Array.isArray(buz) && buz.length > 0 ? buz[0] : '' : buz;
  dispatch({ type: 'setCommodity', payload: isMultiSelect ? [] : '' });
  dispatch({ type: 'setCommodityId', payload: 0 });
  if (businessForRegion === DR_POWER_OTHERS) {
    const defaultOption = { label: 'Power', value: 'Power', key: '2' };
    handleCommodityChange(_e, defaultOption, dispatch, myDispatch, myState);
  }
  setGridLocationOptions([]);
  dispatch({ type: 'setBusiness', payload: buz });
  (dispatch as (action: Action) => void)({ type: 'setBusinessId', payload: isMultiSelect ? businessIds : (businessIds[0] ?? 0) });
  dispatch({ type: 'setGridLocation', payload: '' });
  setBusinessFieldValidations(isMultiSelect, myDispatch, buz);
  regions = getRegions(businessForRegion, state.evp);
  const selectedRegion = buz === DR_POWER_OTHERS ? '' : regions[0];
  dispatch({ type: 'setRegion', payload: selectedRegion });
  dispatch({ type: 'setRegionOptions', payload: regions.map((region) => ({ value: region, label: region })) });
  // SLMT-specific: fetch type of supply options
  if (normalizedEvp === 'slmt' && businessIds.length > 0) {
    myDispatch({ type: 'setIsLoading', payload: true });
    try {
      const resp = await fetchTypeOfSupplyByBusiness(businessIds[0]);
      const options = resp.data.map((item: { TYPE_OF_SUPPLY_ID: number; TYPE_OF_SUPPLY_NAME: string }) => ({ value: item.TYPE_OF_SUPPLY_ID, label: item.TYPE_OF_SUPPLY_NAME }));
      myDispatch({ type: 'setTypeOfSupplyOptions', payload: options });
    } catch {
      notify('error', FETCH_ERROR);
      myDispatch({ type: 'setTypeOfSupplyOptions', payload: [] });
    } finally {
      myDispatch({ type: 'setIsLoading', payload: false });
    }
  }
  if (buz !== DR_POWER_OTHERS) {
    await setCountryOptionsAndCountyValue(
      country,
      myDispatch,
      state,
      selectedRegion,
      dispatch,
      myState,
      { setGridLocationOptions, setIsFetchingGridLocation, setIsFetchingCountry, setCountry },
    );
  } else {
    setCountry('');
  }
};

const setBusinessFieldValidations = (
  isMultiSelect: boolean,
  myDispatch: IDispatch,
  buz: string | string[],
) => {
  if ((isMultiSelect && buz.length > 0) || (!isMultiSelect && buz !== '' && buz !== null)) {
    myDispatch({
      type: 'setisBusinessValid',
      payload: true,
    });
  } else {
    myDispatch({
      type: 'setisBusinessValid',
      payload: false,
    });
  }
};

// Helper function to validate commodity selection
const isCommoditySelectionValid = (
  isMultiSelect: boolean,
  commoditySelected: string | string[],
): boolean => {
  return isMultiSelect
    ? (commoditySelected as string[]).length > 0
    : commoditySelected !== '' && commoditySelected !== null;
};

// Helper function to fetch and prepare tech source options for Power commodity
const fetchTechSourceOptionsForPower = async (myDispatch: IDispatch) => {
  const techSourceResp = await fetchTechSources();
  const techSourceOptns = techSourceResp.data;
  const techSourceOptn: IOptions[] = [];
  const fuelTypeOptn: IOptions[] = [];
  const fuelTypePermitted = ['naturalgas', 'lng'];

  for (const option of techSourceOptns) {
    const techSourceNormalized = option.TECHNOLOGY_SOURCE_NAME.replaceAll(' ', '').toLowerCase();
    const isTechSourcePermitted = techSourcePermitted.includes(techSourceNormalized);
    const isFuelTypePermitted = fuelTypePermitted.includes(techSourceNormalized);

    if (isTechSourcePermitted) {
      techSourceOptn.push({
        value: option.TECHNOLOGY_SOURCE_NAME,
        label: option.TECHNOLOGY_SOURCE_NAME,
      });
    }
    if (isFuelTypePermitted) {
      fuelTypeOptn.push({
        value: option.TECHNOLOGY_SOURCE_NAME,
        label: option.TECHNOLOGY_SOURCE_NAME,
      });
    }
  }

  // Add Gas Fired Generation option
  techSourceOptn.push({
    value: 'Gas Fired Generation',
    label: 'Gas Fired Generation',
  });

  myDispatch({ type: 'setFuelTypeOptions', payload: fuelTypeOptn });
  myDispatch({ type: 'setTechSourceOptions', payload: techSourceOptn });
  myDispatch({ type: 'setTechSourceApiResponse', payload: techSourceOptns });
};

// Helper function to check if commodity is Pipeline Gas or RNG
const isPipelineGasOrRng = (
  isMultiSelect: boolean,
  commoditySelected: string | string[],
): boolean => {
  const commodityLowerCase = isMultiSelect
    ? (commoditySelected as string[]).map((c) => c.toLowerCase())
    : [(commoditySelected as string).toLowerCase()];

  return commodityLowerCase.some((c) => ['pipeline gas', 'rng'].includes(c));
};

// Helper function to disable tech source fields for Pipeline Gas/RNG
const disableTechSourceFields = (dispatch: IDispatch, myDispatch: IDispatch) => {
  myDispatch({ type: 'setEnableTechSourceField', payload: false });
  myDispatch({ type: 'setTechSource', payload: [] });
  myDispatch({ type: 'setShowGasFields', payload: false });
  dispatch({ type: 'setGridLocation', payload: '' });
  myDispatch({ type: 'setDisableGrid', payload: true });
};

// Helper function to enable tech source fields
const enableTechSourceFields = (myDispatch: IDispatch) => {
  myDispatch({ type: 'setEnableTechSourceField', payload: true });
};

export const handleRegionChange = async (
  _e: eventType,
  data: { myState: IManualFormData; option: Options; state: ICalculatorFormState; country: string },
  dispatch: IDispatch,
  myDispatch: IDispatch,
  func: {
    setCountry: StringDispatch;
    setIsFetchingCountry: BooleanDispatch;
    setIsFetchingGridLocation: BooleanDispatch;
    setGridLocationOptions: ArrayDispatch;
  },
) => {
  const { myState, option, state, country } = data;
  const { setGridLocationOptions, setCountry, setIsFetchingCountry, setIsFetchingGridLocation } =
    func;
  const selectedRegion = option.value as string;
  dispatch({
    type: 'setGridLocation',
    payload: '',
  });
  dispatch({
    type: 'setRegion',
    payload: selectedRegion,
  });
  // setting up country options
  await setCountryOptionsAndCountyValue(
    country,
    myDispatch,
    state,
    selectedRegion,
    dispatch,
    myState,
    {
      setGridLocationOptions,
      setIsFetchingGridLocation,
      setIsFetchingCountry,
      setCountry,
    },
  );
};

export const handleCommodityChange = async (
  _e: eventType,
  selectedOption: Options,
  dispatch: IDispatch,
  myDispatch: IDispatch,
  myState?: IManualFormData,
) => {
  // Handle both single-select (string) and multi-select (array) modes
  const isMultiSelect = Array.isArray(_e);
  const commoditySelected = isMultiSelect ? (_e as string[]) : (selectedOption.value as string);
  const commodityLabels = Array.isArray(commoditySelected)
    ? commoditySelected
    : [commoditySelected];
  const commodityIds = myState
    ? commodityLabels
        .map(
          (label) => {
            const foundOption = myState.commodityOptions.find((opt) => String(opt.label) === String(label));
            return foundOption?.value;
          }
        )
        .filter((value): value is string | number => value !== undefined && value !== null)
        .map((value) => Number(value))
        .filter((value) => !Number.isNaN(value))
    : [];

  // Invalidate the form when commodity changes
  dispatch({ type: 'setIsFormValidated', payload: false });

  const isValidSelection = isCommoditySelectionValid(isMultiSelect, commoditySelected);

  if (isValidSelection) {
    myDispatch({ type: 'setisCommodityValid', payload: true });
    dispatch({ type: 'setCommodity', payload: commoditySelected });
    (dispatch as (action: Action) => void)({
      type: 'setCommodityId',
      payload: isMultiSelect ? commodityIds : (commodityIds[0] ?? 0),
    });

    // Fetch tech source options only for Power commodity
    if (!isMultiSelect && (commoditySelected as string).toLowerCase() === 'power') {
      await fetchTechSourceOptionsForPower(myDispatch);
    }
  } else {
    myDispatch({ type: 'setisCommodityValid', payload: false });
  }

  // Handle Pipeline Gas or RNG - disable tech source fields
  if (isPipelineGasOrRng(isMultiSelect, commoditySelected)) {
    disableTechSourceFields(dispatch, myDispatch);
  } else {
    enableTechSourceFields(myDispatch);
  }
};


export const handleTypeOfSupplyChange = async (
  _e: eventType,
  selectedOption: Options,
  dispatch: IDispatch,
  myDispatch: IDispatch,
  state: ICalculatorFormState,
  func: {
    setCountry: StringDispatch;
    setIsFetchingCountry: BooleanDispatch;
    setGridLocationOptions: ArrayDispatch;
    setIsFetchingGridLocation: BooleanDispatch;
    setCountryAssetOfSupplyOptions?: ArrayDispatch;
  },
) => {
  const { setCountry } = func;
  const typeOfSupplySelected = selectedOption.key as string;
  dispatch({ type: 'setIsFormValidated', payload: false });
  if (typeOfSupplySelected !== '' && typeOfSupplySelected !== null) {
    myDispatch({ type: 'setIsTypeOfSupplyValid', payload: true });
    dispatch({ type: 'setTypeOfSupply', payload: selectedOption.value as string });
    dispatch({ type: 'setTypeOfSupplyId', payload: Number(typeOfSupplySelected) });
    
    // SLMT-specific: fetch country asset of supply options
    if (normalizeEvpName(state.evp) === 'slmt') {
      try {
        const resp = await fetchCountryAssetOfSupplyByType(Number(typeOfSupplySelected));
        const options = resp.data.map((item: { COUNTRY_ASSET_OF_SUPPLY_ID: number; COUNTRY_ASSET_OF_SUPPLY_NAME: string }) => ({ value: item.COUNTRY_ASSET_OF_SUPPLY_ID, label: item.COUNTRY_ASSET_OF_SUPPLY_NAME }));
        myDispatch({ type: 'setCountryAssetOfSupplyOptions', payload: options });
        dispatch({ type: 'setCountryOptions', payload: options });
        if (options.length <= 1) {
          const firstOption = options[0];
          setCountry(firstOption.label);
          dispatch({ type: 'setCountry', payload: firstOption.label });
          dispatch({ type: 'setCountryId', payload: Number(firstOption.value) });
        } else {
          setCountry('');
          dispatch({ type: 'setCountry', payload: '' });
          dispatch({ type: 'setCountryId', payload: 0 });
        }
      } catch {
        notify('error', FETCH_ERROR);
        myDispatch({ type: 'setCountryAssetOfSupplyOptions', payload: [] });
        dispatch({ type: 'setCountryOptions', payload: [] });
      }
    } else {
      setCountry('');
      dispatch({ type: 'setCountry', payload: '' });
      dispatch({ type: 'setCountryId', payload: 0 });
    }
    // For non-SLMT, fallback to constant mapping
    if (normalizeEvpName(state.evp) !== 'slmt') {
      const countryList = TYPE_OF_SUPPLY_COUNTRY_MAPPING[typeOfSupplySelected] || [];
      const countryOpts = countryList.map((country, index) => ({ value: String(index + 1), label: country }));
      dispatch({ type: 'setCountryOptions', payload: countryOpts });
    }
  } else {
    myDispatch({ type: 'setIsTypeOfSupplyValid', payload: false });
  }
};

export const handleCountyChange = (
  _e: eventType,
  selectedOption: Options,
  country: string,
  param: {
    dispatch: IDispatch;
    myDispatch: IDispatch;
    setCountry: StringDispatch;
    myState: IManualFormData;
    setIsFetchingGridLocation: BooleanDispatch;
    state: ICalculatorFormState;
    setGridLocationOptions: ArrayDispatch;
  },
) => {
  const {
    dispatch,
    myDispatch,
    setCountry,
    myState,
    setIsFetchingGridLocation,
    state,
    setGridLocationOptions,
  } = param;
  const countrySelected = selectedOption.value;
  // invalidate the form if value is changed from one option to another, this will lead to hide the step two table
  if (country !== '') {
    dispatch({ type: 'setIsFormValidated', payload: false });
  }

  if (countrySelected !== '' || countrySelected !== null) {
    myDispatch({ type: 'setisCountryValid', payload: true });

    setCountry(countrySelected as string);
    dispatch({
      type: 'setCountry',
      payload: countrySelected as string,
    });

    dispatch({
      type: 'setCountryId',
      payload: Number(selectedOption.key),
    });
    dispatch({ type: 'setGridLocation', payload: '' });
    // at this point, region and country will have data. just check if tech source has GRID selected.
    if (myState.techSource.includes(GRID)) {
      void fetchGridLocationData(
        { setIsFetchingGridLocation, setGridLocationOptions, state, dispatch, myDispatch },
        country,
        countrySelected as string,
      );
    }

    dispatch({ type: 'setGridLocation', payload: '' });
  } else {
    myDispatch({ type: 'setisCountryValid', payload: false });
  }
};

export const handleTechSourceChange = (
  techSources: eventType,
  _selectedOption: Options,
  gridLocationOptions: Array<{ value: string; label: string }>,
  params: {
    dispatch: IDispatch;
    myDispatch: IDispatch;
    state: ICalculatorFormState;
    country: string;
    myState: IManualFormData;
    setIsFetchingGridLocation: BooleanDispatch;
    setGridLocationOptions: ArrayDispatch;
  },
) => {
  const {
    dispatch,
    myDispatch,
    state,
    country,
    myState,
    setIsFetchingGridLocation,
    setGridLocationOptions,
  } = params;
  if (
    !['GAS_FIRED_GENERATION', 'LNG', NATURAL_GAS].some((element) =>
      (techSources as string[]).includes(element),
    )
  ) {
    dispatch({
      type: 'setFuelType',
      payload: '',
    });
  }
  const [hasSelectedTechSrcLNGOrNaturalGas, currentFuelTypeVal, currentFuelTypeId] =
    setUpVarsForGFP(state);
  const selectedTechSourceValues = getTechSrcValues(
    techSources as string[],
    hasSelectedTechSrcLNGOrNaturalGas,
    currentFuelTypeVal,
    currentFuelTypeId,
    myState,
  ) as Array<{ value: string; id: number }>;
  // invalidate the form if value is changed from one option to another, this will lead to hide the step two table
  dispatch({ type: 'setIsFormValidated', payload: false });
  dispatch({
    type: 'setSelectedTechnologySources',
    payload: selectedTechSourceValues,
  });
  myDispatch({ type: 'setTechSource', payload: techSources as string[] });
  // show fields related to Gas Fired Generation
  if ((techSources as string[]).includes(GAS_FIRED_GENERATION)) {
    myDispatch({ type: 'setShowGasFields', payload: true });
  } else {
    myDispatch({ type: 'setShowGasFields', payload: false });
  }

  // if the last value selected to GRID. means currently selected value is GRID else everytime a tech source is selected,
  // this api will hit if techsource has GRID in it
  const isLastTechSrcGrid =
    (techSources as string[])[(techSources as string[]).length - 1] === GRID;
  const isRegionAndCountryNotEmpty = state.region !== '' && country !== '';
  if (isLastTechSrcGrid && isRegionAndCountryNotEmpty) {
    void fetchGridLocationData(
      { setIsFetchingGridLocation, setGridLocationOptions, state, dispatch, myDispatch },
      country,
    );
  }
  updateGridLocationFieldView(techSources as string[], dispatch, myDispatch, gridLocationOptions);

  // reset gridLocation state value if GRID is not present in the tech sources. this is typically when GRID was present and removed from the list
  if (!(techSources as string[]).includes('Grid') && state.gridLocationId !== 0) {
    dispatch({
      type: 'setGridLocationId',
      payload: 0,
    });
  }
};

export const handleGridLocationChange = (
  e: eventType,
  selectedOption: Options,
  dispatch: IDispatch,
  myDispatch: IDispatch,
) => {
  myDispatch({
    type: 'setIsGridLocationValid',
    payload: true,
  });
  dispatch({
    type: 'setGridLocation',
    payload: e as string,
  });
  dispatch({
    type: 'setGridLocationId',
    payload: Number(selectedOption.key),
  });
};

export const maxLengthCheck = (event: TChangeEvent) => {
  if (event.target.value.length > event.target.maxLength) {
    event.target.value = event.target.value.slice(0, event.target.maxLength);
    return true;
  }
  return false;
};

const   getRegions = (business: string | string[], evp: string) => {
  let regions: string[] = [];
  const normalizedEvp = normalizeEvpName(evp);

  switch (true) {
    case business.includes(AMERICAS):
      regions = [AMERICAS];
      break;
    case business.includes(EUROPE):
      regions = [EUROPE];
      break;
    case business.includes(ASIA):
      regions = [ASIA];
      break;
    case business.includes(AUSTRALIA):
      regions = [AUSTRALIA];
      break;
    case business === DR_POWER_OTHERS:
      regions = [AMERICAS, EUROPE, ASIA];
      break;
    case normalizedEvp === 'tsproducts':
      regions = [GLOBAL, ASIA, EUROPE, AMERICAS];
      break;
    default:
    // handle any other cases here
  }
  return regions;
};
const setCountryOptionsAndCountyValue = async (
  country: string,
  myDispatch: IDispatch,
  state: ICalculatorFormState,
  region: string,
  dispatch: IDispatch,
  myState: IManualFormData,
  func: {
    setGridLocationOptions: ArrayDispatch;
    setIsFetchingGridLocation: BooleanDispatch;
    setIsFetchingCountry: BooleanDispatch;
    setCountry: StringDispatch;
  },
) => {
  const { setGridLocationOptions, setIsFetchingGridLocation, setIsFetchingCountry, setCountry } =
    func;
  let countryOpts = [{ label: '', value: '' }];
  setIsFetchingCountry(true);
  const normalizedEvp = normalizeEvpName(state.evp);
  try {
    // For SLMT, use TYPE_OF_SUPPLY_COUNTRY_MAPPING based on selected Type of Supply
    if (normalizedEvp === 'slmt') {
      const typeOfSupply = state.typeOfSupply || 'Shell Asset'; // Default to Shell Asset
      const countryList = TYPE_OF_SUPPLY_COUNTRY_MAPPING[typeOfSupply] || [];
      countryOpts = countryList.map((country, index) => ({
        value: String(index + 1),
        label: country,
      }));
    } else {
      const countryResp = await fetchCountries(region);
      const countryRespData = countryResp.data;
      const isTSProducts = normalizedEvp === 'tsproducts';
      const filteredCountries = isTSProducts
        ? countryRespData
        : countryRespData.filter((ctry: ICountry) => {
            return possibleCountryOptions[region as keyof typeof possibleCountryOptions]?.includes(
              capitalizeFirstLetter(ctry.COUNTRY_NAME),
            );
          });

      countryOpts = filteredCountries
        .sort((a: ICountry, b: ICountry) => a.COUNTRY_NAME.localeCompare(b.COUNTRY_NAME))
        .map((ctry: ICountry) => {
          return {
            value: ctry.COUNTRY_ID,
            label: capitalizeFirstLetter(ctry.COUNTRY_NAME),
          };
        });
    }
    // default the Country field to the value if countryOpts has only one country
    if (countryOpts.length === 1) {
      setCountry(countryOpts[0].label);
      // As country is set to a value here, make API call for Grid Location if GRID is already selected in Technology sources
      if (myState.techSource.includes(GRID)) {
        await fetchGridLocationData(
          { setIsFetchingGridLocation, setGridLocationOptions, state, dispatch, myDispatch },
          country,
          countryOpts[0].label,
          region,
        );
      }
      dispatch({
        type: 'setCountryId',
        payload: countryOpts[0].value,
      });
    } else {
      setCountry('');
      dispatch({
        type: 'setCountryId',
        payload: 0,
      });
    }
    dispatch({ type: 'setCountryOptions', payload: countryOpts });
  } catch {
    notify('error', FETCH_ERROR);
  }
  setIsFetchingCountry(false);
  return countryOpts;
};

const setUpVarsForGFP = (state: ICalculatorFormState) => {
  let hasSelectedTechSrcLNGOrNaturalGas = false;
  let currentFuelTypeVal = '';
  let currentFuelTypeId = 0;
  for (const item of state.selectedTechnologySources) {
    if (item.value === 'LNG' || item.value === NATURAL_GAS) {
      hasSelectedTechSrcLNGOrNaturalGas = true;
      currentFuelTypeVal = item.value;
      currentFuelTypeId = item.id;
      break;
    }
  }
  return [hasSelectedTechSrcLNGOrNaturalGas, currentFuelTypeVal, currentFuelTypeId];
};
type StringNumberBoolean = string | number | boolean;
const getTechSrcValues = (
  techSources: string[],
  hasSelectedTechSrcLNGOrNaturalGas: StringNumberBoolean,
  currentFuelTypeVal: StringNumberBoolean,
  currentFuelTypeId: StringNumberBoolean,
  myState: IManualFormData,
) => {
  return techSources.map((name: string) => {
    if (name !== GAS_FIRED_GENERATION) {
      const techSource: ITechSrcApiResponseObj = myState.techSourceApiResponse.find(
        (obj) => obj.TECHNOLOGY_SOURCE_NAME === name,
      ) as ITechSrcApiResponseObj;
      if (techSource) {
        return { value: name, id: techSource.TECHNOLOGY_SOURCE_ID };
      } else {
        return { value: name, id: -1 };
      }
    }
    // this condition checks if tech source already has a fuel type selected and then tech source change happens.
    // here if gas fired generation is there then no need to update the selected tech source with 'Gas Fired Generation' again
    else if (hasSelectedTechSrcLNGOrNaturalGas) {
      return { value: currentFuelTypeVal, id: currentFuelTypeId };
    } else {
      return { value: name, id: -1 };
    }
  });
};

const updateGridLocationFieldView = (
  techSources: string | string[],
  dispatch: IDispatch,
  myDispatch: IDispatch,
  gridLocationOptions: { value: string; label: string }[],
) => {
  // enable/disable GRID LOCAITON field
  if (techSources.includes(GRID)) {
    // if techsource has GRID in it
    if (gridLocationOptions.length === 1) {
      // but gridLocation has only one data
      myDispatch({
        type: 'setDisableGrid',
        payload: true,
      });
    } else {
      myDispatch({
        type: 'setDisableGrid',
        payload: false,
      });
    }
  } else {
    myDispatch({
      type: 'setDisableGrid',
      payload: true,
    });

    dispatch({
      type: 'setGridLocation',
      payload: '',
    });
  }
};

interface GridLocationDataParams {
  setIsFetchingGridLocation: BooleanDispatch;
  setGridLocationOptions: ArrayDispatch;
  state: ICalculatorFormState;
  dispatch: IDispatch;
  myDispatch: IDispatch;
}
const fetchGridLocationData = async (
  params: GridLocationDataParams,
  country: string,
  countryVal?: string,
  region?: string,
) => {
  const { setIsFetchingGridLocation, setGridLocationOptions, state, dispatch, myDispatch } = params;
  try {
    setIsFetchingGridLocation(true);
    const gridLocationResponse = await fetchGridLocations(
      region ?? state.region, // if region is undefined then it will take state.region
      countryVal ?? country, // if countryVal is undefined then it will take country
    );
    const gridLocation: IGridLocation[] = gridLocationResponse.data;
    const gridLocationOptions: Array<{ value: string; label: string }> = [];

    gridLocation
      .filter((gridData: IGridLocation) => gridData.Grid !== 'CAISO') // Exclude CAISO
      .forEach((gridData: IGridLocation) => {
        gridLocationOptions.push({
          value: String(gridData.GRID_ID),
          label: gridData.Grid,
        });
      });

    // Sort alphabetically by label
    gridLocationOptions.sort((a, b) => a.label.localeCompare(b.label));
    setGridLocationOptions(gridLocationOptions);
    // if grid location has only one option then set the value as default to the field and disable the field
    if (gridLocationOptions.length === 1) {
      dispatch({
        type: 'setGridLocation',
        payload: gridLocationOptions[0].label,
      });
      dispatch({
        type: 'setGridLocationId',
        payload: gridLocationOptions[0].value,
      });

      myDispatch({
        type: 'setDisableGrid',
        payload: true,
      });
    } else {
      myDispatch({
        type: 'setDisableGrid',
        payload: false,
      });
    }
  } catch {
    notify('error', FETCH_ERROR);
  }
  setIsFetchingGridLocation(false);
};

export const fetchDropDownData = async (myDispatch: IDispatch) => {
  myDispatch({
    type: 'setIsLoading',
    payload: true,
  });
  try {
    const evpOptn = [];
    const assetTypeOptn = [];
    const evpResp = await fetchEvp();

    // create options for EVP dropdown field
    for (const option of evpResp.data) {
      evpOptn.push({
        key: option.EVP_ID,
        value: option.EVP_NAME,
        label: option.EVP_NAME,
      });
    }
    assetTypeOptn.push(
      { value: 'Greenfield', label: 'Greenfield' },
      { value: 'Brownfield', label: 'Brownfield' },
    );

    myDispatch({ type: 'setEvpOptions', payload: evpOptn });
    myDispatch({ type: 'setAssetTypeOptions', payload: assetTypeOptn });
  } catch {
    notify('error', FETCH_ERROR);
    myDispatch({
      type: 'setIsLoading',
      payload: false,
    });
  }
  myDispatch({
    type: 'setIsLoading',
    payload: false,
  });
};

export const setUom = (
  business: string | string[],
  commodity: string | string[],
  evp: string,
  myDispatch: IDispatch,
  dispatch: IDispatch,
) => {
  const uom = setUnitOfMeasure(business, commodity, evp);
  myDispatch({
    type: 'setUomVal',
    payload: uom,
  });
  dispatch({
    type: 'setUomVal',
    payload: uom,
  });
};

// Helper to check if commodity matches specific types
const isCommodityMatch = (commodityStr: string, types: string[]) => types.includes(commodityStr);

// Helper to determine UOM based on business and commodity combination
const getUomByBusinessAndCommodity = (
  business: string | string[],
  commodityStr: string,
  evp: string,
) => {
  const isPgOrRng = isCommodityMatch(commodityStr, [PIPELINE_GAS, RNG]);
  const isPgOrPwOrRng = isCommodityMatch(commodityStr, [PIPELINE_GAS, POWER, RNG]);
  const normalizedEvp = normalizeEvpName(evp);
  // Shell Energy logic
  if (normalizedEvp === 'shellenergy') {
    if (business.includes('Americas') && isPgOrRng) return MMBTU;
    if (business.includes('Asia') && isPgOrRng) return MMBTU;
    if (business.includes('Australia') && isPgOrRng) return PJ;
    if (business.includes('Europe') && isPgOrPwOrRng) return MWH;
  }
  return null;
};

const setUnitOfMeasure = (
  business: string | string[],
  commodity: string | string[],
  evp: string,
) => {
  const normalizedEvp = normalizeEvpName(evp);
  // if evp = SLMT, UOM -> TBTU
  if (normalizedEvp === 'slmt') return TBTU;

  // if evp = T&S Products, UOM -> KBBL
  if (normalizedEvp === 'tsproducts') return KBBL;

  // Convert commodity to string for comparison
  const commodityStr = Array.isArray(commodity)
    ? commodity.length > 0
      ? commodity[0]
      : ''
    : commodity;

  // Check business and commodity combinations
  const uom = getUomByBusinessAndCommodity(business, commodityStr, evp);

  return uom || MWH; // default UOM
};
